<template>
  <div class="popups-newsletter">
    <div v-if="showConfirmation" class="p-4 text-sm font-bold">
      <div class="text-primary">Danke</div>
      <div>
        Vielen Dank für Ihr Interesse an unserem Newsletter. In Kürze erhalten Sie eine
        E-Mail mit einem Aktivierungslink. Bitte bestätigen Sie Ihre Anmeldung durch
        einen Klick auf diesen Link.
      </div>
    </div>
    <form
      v-else
      class="newsletter-form gap-4"
      :style="'background-image:url(' + formbg + ')'"
      @submit.prevent="validateForm($event as SubmitEvent)"
    >
      <div
        class="flex flex-col lg:grid lg:grid-cols-2 gap-4 text-sm bg-secondary lg:bg-transparent rounded-lg p-4 lg:p-0"
      >
        <div class="text-primary text-2xl col-span-2">expert Newsletter</div>
        <div class="text-sm col-span-2">
          Nimm an unseren zahlreichen Gewinnspielen und Aktionen teil, bleibe stets über
          aktuelle Angebote und Techniktrends informiert.
        </div>
        <div class="col-span-2">
          <label for="nlanrede" class="text-xs py-2 block">Anrede*</label>
          <select id="nlanrede" class="inp border-r-[14px] border-white shadow">
            <option value="">Anrede</option>
            <option value="male">Herr</option>
            <option value="female">Frau</option>
            <option value="diverse">Divers</option>
            <option value="company">Firma</option>
            <option value="family">Familie</option>
          </select>
        </div>
        <div
          v-for="entry in submissionFormFields"
          :key="entry.id"
          class="relative col-span-2"
        >
          <label
            :for="entry.id"
            class="text-xs py-2 block"
            v-text="entry.displayName + '*'"
          />
          <input
            :id="entry.id"
            v-model="entry.hydratedValue"
            :type="entry.type"
            :aria-label="entry.displayName"
            class="inp rounded required"
            :name="entry.id"
            :placeholder="entry.displayName"
          >
          <div class="error-message" v-text="entry.errorMessage" />
        </div>
        <div class="col-span-2">
          <hr>
          <input
            type="text"
            aria-label="Fachmarkt"
            class="inp rounded !bg-secondary-dark"
            :value="currentStore.name + ' - ' + currentStore.zip + ' - ' + currentStore.city"
            disabled
            readonly
          >
          <hr>
        </div>
        <div class="col-span-2">
          <div :class="captchaError ? 'border border-error' : ''">
            <NuxtTurnstile
              ref="turnstile"
              v-model="token"
              class="cf-turnstile"
              :options="{ theme: 'light', language: 'de-de' }"
            />
            <div
              v-if="captchaError"
              class="captcha-error-message text-error py-2 px-2 text-center border border-error border-dashed bg-error-light"
            >
              {{ result }}
            </div>
          </div>
        </div>
        <div class="col-span-2">
          <button class="btn-primary w-full font-bold rounded-full shadow py-4">
            Jetzt kostenlos registrieren
          </button>
        </div>
        <div class="text-right col-span-2 text-sm">Pflichtfelder *</div>
      </div>
    </form>
  </div>
</template>
<script setup lang="ts">
import formbg from "~/assets/img/Footer/NL_BG.png";
import { useCurrentUserState, useCurrentStore } from "~/composables/states";
type CurrentUser = {
  email?: string;
  firstName?: string;
  lastName?: string;
  storeId?: string;
  csrfToken?: string;
} | null;

const currentStore = useCurrentStore();
const token = defineModel<string>();
const turnstile = ref<Turnstile.Turnstile>();
const result = ref("");
const captchaError = ref(false);
const showConfirmation = ref(false);
const currentUserData = useCurrentUserState() as Ref<CurrentUser>;
const submissionFormFields = ref();

watchEffect(() => {
  submissionFormFields.value = [
    {
      id: "email",
      type: "email",
      displayName: "E-Mail",
      hydratedValue: currentUserData?.value?.email ?? null,
      errorMessage: "Bitte geben Sie eine E-Mail an",
    },
    {
      id: "firstName",
      type: "text",
      displayName: "Vorname",
      hydratedValue: currentUserData?.value?.firstName ?? null,
      errorMessage: "Bitte geben Sie einen Vornamen an",
    },
    {
      id: "lastName",
      type: "text",
      displayName: "Nachname",
      hydratedValue: currentUserData?.value?.lastName ?? null,
      errorMessage: "Bitte geben Sie einen Nachnamen ein",
    },
  ];
});

async function validateForm(event: SubmitEvent) {
  captchaError.value = false;
  if (token.value) {
    const form = event.target as HTMLFormElement | null;
    if (!form) return;

    const fields = form.querySelectorAll(
      "input.required"
    ) as NodeListOf<HTMLInputElement>;
    let validForm = true;

    fields.forEach((element: HTMLInputElement) => {
      if (element.value.trim()) {
        element.classList.remove("error");
      } else {
        element.classList.add("error");
        validForm = false;
      }
    });

    if (validForm == true) {
      let checkOnRequiredFields = true;
      const firstNameEl = form.querySelector<HTMLInputElement>("#firstName");
      const lastNameEl = form.querySelector<HTMLInputElement>("#lastName");
      const emailEl = form.querySelector<HTMLInputElement>("#email");

      if (
        (firstNameEl?.value ?? "") == "" ||
        (lastNameEl?.value ?? "") == "" ||
        (emailEl?.value ?? "") == ""
      ) {
        checkOnRequiredFields = false;
      }

      const email = emailEl?.value ?? "";

      if (checkOnRequiredFields == true) {
        const formdata = {
          firstName: firstNameEl?.value ?? "",
          lastName: lastNameEl?.value ?? "",
          email: email,
          captcha: token.value,
        };

        const { status, error } = await useFetch<Response>(
          "/api/neo/frontend/_api/exp-newsletter/subscribe",
          {
            method: "POST",
            headers: {
              "bt-use-user-auth": "true",
              "csrf-token": currentUserData.value?.csrfToken as string,
            },
            body: formdata,
          }
        );

        if (status.value == "success") {
          showConfirmation.value = true;
        } else {
          console.error("Failed to register user for newsletter", error.value);
          turnstile.value?.reset();
          captchaError.value = true;
        }
      }
    } else {
      console.error("form is invalid");
    }
  } else {
    turnstile.value?.reset();
    captchaError.value = true;
  }
}
</script>
<style scoped lang="postcss">
.teaser {
  @apply px-4 lg:px-12 py-8 flex flex-col gap-4 lg:gap-8 rounded-3xl relative bg-cover bg-no-repeat;
}
.newsletter-form {
  @apply bg-no-repeat bg-left-top lg:bg-right-top bg-cover lg:p-4 grid grid-cols-1 lg:grid-cols-2;
}
input[type="text"],
input[type="email"] {
  &:read-only {
    @apply bg-secondary pointer-events-none;
  }
  &:disabled {
    @apply bg-secondary pointer-events-none;
  }
  & + .error-message {
    @apply absolute  text-xs hidden text-error;
  }
  &.error {
    @apply border-error;
    & + .error-message {
      @apply block;
    }
  }
}
</style>
